/**
 * Insert a figure into the article body, at the caret (#1744 point 3, on
 * #1679's accessory seam).
 *
 * ── WHY THIS COULD NOT BE BUILT BEFORE, AND WHAT CHANGED ───────────────────
 * `KbArticleAssets` has carried a "copy `{{fig:CHEV-01}}` for the body" button
 * as an honest temporary fallback, and its header said why: a TRUE in-editor
 * insert meant extending `packages/ui`'s `FieldEditor`, whose `slot` is bound to
 * `kind === 'ref'`, and widening that changes what every host renders for every
 * entity. That is a platform-shape decision, and inventing one from a record
 * panel is the same mistake as inventing a record-action registrar.
 *
 * #1679 made the decision, and made it BETTER than the widening I would have
 * asked for. `FieldEditor.actions` is a separate seam from `slot`, and its own
 * comment gives the reason: *"`slot` REPLACES the control for `ref`, and
 * overloading it to also decorate a text field would make a missing slot
 * capable of erasing the real editor. These sit BESIDE the editor and hand the
 * host the staged value and the caret."*
 *
 * The staged value and the caret are exactly what an insert needs, and neither
 * was reachable from a panel beside the form. So this replaces the copy button
 * rather than joining it.
 *
 * ── THE OFFER IS REMOVED, NEVER DISABLED ───────────────────────────────────
 * `FieldAction.capability`'s own doctrine: *"A capability the host does not
 * permit removes the control ENTIRELY rather than disabling it: an offer that
 * can never be taken is furniture."* Three cases where it cannot be taken, and
 * all three withhold the capability rather than showing a dead button:
 *
 *   · the record is not a knowledge article;
 *   · the caller has no WRITE authority — `service.kb_article_asset_states`
 *     takes write and refuses otherwise, so the authority answer comes from the
 *     server's refusal rather than from a guess this file makes;
 *   · the article has no VERIFIED figure. A marker naming a pending or
 *     quarantined figure resolves to nothing for every reader, so offering the
 *     insert would hand the author a broken reference behind a working button.
 *     `KbArticleAssets` is where that state is explained; this is not the place
 *     to explain it a second time, differently.
 *
 * ── AND THE MARKER IS SPLICED, NOT APPENDED ────────────────────────────────
 * `{{fig:…}}` is INLINE — `parseArticleBody` scans it anywhere in the text and
 * has no notion of a block. So the insert is a plain splice over the caret's
 * range, which also makes "select a word, insert a figure" replace the
 * selection, the behaviour every text editor already taught the author.
 */
import { useCallback, useRef, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FIGURE_ID, Lookup, type FieldAction, type LookupItem, type LookupSection } from '@solum/ui'
import { useSolum } from '../data/client'
import type { KbAssetState } from './KbArticleAssets'

/** The capability an insert offer needs. Named once, used by host and test. */
export const KB_INSERT_FIGURE = 'kb.insert_figure'

/** The entity whose body carries figure markers. */
const KB_ARTICLE = 'service_kb_article'

/** The column the offer attaches to. Not every text field wants a figure. */
const BODY = 'body'

export interface KbFigureSeam {
  /** What `EntityForm` asks per column. Undefined = this column offers nothing. */
  fieldActions?: (field: string) => readonly FieldAction[] | undefined
  /** `null` = no capability model. An array = exactly what is permitted. */
  permittedCapabilities: readonly string[] | null
  /** The picker, rendered by the host beside the form. */
  picker: ReactNode
}

/**
 * Splice a marker over the caret's range.
 *
 * Exported so the arithmetic is testable without a DOM: an off-by-one here
 * silently eats a character of somebody's article.
 *
 * ── THE NEIGHBOURING BRACE, WHICH IS NOT A NICETY ──────────────────────────
 * `parseArticleBody` requires the braces to be EXACT — its own comment says
 * *"`{{{fig:a}}}` is prose that happens to contain a marker-shaped run"* — and
 * it checks the characters either side:
 *
 *     const exact = source[at - 1] !== '{' && source[at + m[0].length] !== '}'
 *
 * So splicing next to a brace produces a token that renders as RAW TEXT. A
 * knowledge article for a Postgres platform is an unusually likely place to
 * find a `{`, and the worst case is not cosmetic: inserting at offset 2 of an
 * existing `{{fig:A}}` yields `{{{{fig:CHEV-01}}fig:A}}`, which resolves
 * NEITHER figure — the author's existing one is destroyed by the act that was
 * meant to add a second.
 *
 * A single space on the offending side is enough, and it is the smallest change
 * to the author's text that makes the marker real. Found by review; the test
 * that claimed to hold this only ever parsed a splice into an EMPTY body.
 */
export function spliceMarker(
  value: string, start: number, end: number, assetKey: string,
): string {
  const marker = `{{fig:${assetKey}}}`
  let from = Math.max(0, Math.min(start, value.length))
  let to = Math.max(from, Math.min(end, value.length))

  /* A caret INSIDE an existing marker steps out to its end.
     The brace spacing below saves the marker being written; it cannot save the
     one being written INTO. `spliceMarker('{{fig:A}}', 2, 2, …)` splits `A`'s
     token in half whatever is done with whitespace, so the author's existing
     figure is destroyed by the act meant to add a second. The caret can be
     there — a person can click anywhere in a textarea — so the insertion point
     moves rather than the text breaking. Inserting AFTER is also what the
     author almost certainly meant. */
  for (const m of value.matchAll(new RegExp(String.raw`\{\{fig:${FIGURE_ID}\}\}`, 'g'))) {
    const at = m.index
    const end_ = at + m[0].length
    if (from > at && from < end_) { from = end_; to = Math.max(to, end_) }
  }

  const before = value.slice(0, from)
  const after = value.slice(to)
  return before
    + (before.endsWith('{') ? ' ' : '')
    + marker
    + (after.startsWith('}') ? ' ' : '')
    + after
}

export function useKbFigureSeam(entity: string, id: string): KbFigureSeam {
  const { client } = useSolum()
  const raw = client.raw()
  const isArticle = entity === KB_ARTICLE

  const assets = useQuery({
    queryKey: ['kb', 'asset-states', id],
    queryFn: () => raw.rpcTable<KbAssetState>(
      'kb_article_asset_states', { p_article_id: id }, 'service'),
    /* The door takes WRITE authority, so a refusal is the honest answer for a
       colleague who may read the article and not change it — and it must not be
       retried into a wall. The refusal is also what withholds the capability
       below, which is why no separate authority read is needed here.

       `id !== 'new'` because `RecordBody` calls this seam before its query gate
       and passes the literal `'new'` on a create form. Without it every create
       fired `kb_article_asset_states(p_article_id => 'new')` — a guaranteed
       uuid-parse refusal, once per create, saying nothing. The house precedent
       is two files away: `data/queries.ts` gates on `id !== 'new'` and
       `RecordBody` itself uses `!isNew && id`. And an article that does not
       exist has no figures, so there is nothing the offer could name. */
    enabled: isArticle && Boolean(id) && id !== 'new',
    retry: false,
  })

  const figures = (assets.data ?? []).filter(
    (a) => a.kind === 'figure' && a.verification_state === 'verified')

  const [open, setOpen] = useState(false)
  /* The action is async: `run` returns a promise the PICKER resolves. Held in a
     ref rather than in state because resolving it must not itself re-render —
     the host writes the value, and that is the render. */
  const pending = useRef<{
    resolve: (next: string | null) => void
    ctx: { value: string; selection: { start: number; end: number } }
  } | null>(null)

  const finish = useCallback((assetKey: string | null) => {
    const p = pending.current
    pending.current = null
    setOpen(false)
    if (!p) return
    /* `null` means "no change", and `FieldAction.run`'s contract is explicit
       that the field must not be marked dirty for a cancelled dialog. */
    p.resolve(assetKey === null ? null
      : spliceMarker(p.ctx.value, p.ctx.selection.start, p.ctx.selection.end, assetKey))
  }, [])

  const insert: FieldAction = {
    key: 'insert-figure',
    label: 'Insert figure',
    capability: KB_INSERT_FIGURE,
    run: (ctx) => new Promise<string | null>((resolve) => {
      pending.current = { resolve, ctx }
      setOpen(true)
    }),
  }

  const permitted = !isArticle || figures.length === 0 ? [] : [KB_INSERT_FIGURE]

  const search = (query: string): LookupSection[] => {
    const q = query.trim().toLowerCase()
    const items: LookupItem[] = figures
      .filter((f) => !q
        || f.asset_key.toLowerCase().includes(q)
        || (f.alt_text ?? '').toLowerCase().includes(q))
      .map((f) => ({
        id: f.asset_key,
        primary: f.asset_key,
        /* The alt text is the only thing that distinguishes two figures to a
           person choosing between them, and a figure WITHOUT it is a defect the
           assets panel already names — so the absence is said here too rather
           than rendered as a blank line. */
        secondary: f.alt_text ?? 'No alt text — a screen reader gets nothing from this figure.',
      }))
    return items.length ? [{ title: 'Verified figures', items }] : []
  }

  return {
    fieldActions: isArticle ? (field) => (field === BODY ? [insert] : undefined) : undefined,
    permittedCapabilities: isArticle ? permitted : null,
    picker: open ? (
      <Lookup
        presentation="dialog"
        title="Insert a figure"
        hint="Only verified figures are offered — a marker naming a pending or quarantined figure renders as nothing for every reader."
        placeholder="Search by figure id or alt text"
        mode="single"
        minChars={0}
        search={search}
        onPick={(item) => { finish(String(item.id)) }}
        onClose={() => { finish(null) }}
        testid="kb-figure-picker"
      />
    ) : null,
  }
}
